import { observable, makeObservable } from "mobx";
import Analysis from "./Analysis";
import PlannedProgressEntry from "./PlannedProgressEntry";
import { ApplyDistributionToTask } from "../operations/TaskOperations";
import { currencyFormat, decimalAsPercentFormat } from "../util/Formatters";

export default class TaskAnalysis {
  project;
  task;
  id;
  name;
  distribution;
  plannedCost;
  plannedDuration;
  plannedStartDate;
  plannedEndDate;
  analysis;
  actualProgress;
  plannedProgress;
  displayIndex;
  code;

  constructor(
    project,
    taskPackage,
    id,
    name,
    distribution,
    plannedCost,
    plannedDuration,
    plannedStartDate,
    plannedEndDate,
    analysis,
    displayIndex
  ) {
    this.project = project;
    this.taskPackage = taskPackage;
    this.id = id;
    this.name = name;
    this.distribution = distribution;
    this.plannedCost = plannedCost;
    this.plannedDuration = plannedDuration;
    this.plannedStartDate = plannedStartDate;
    this.plannedEndDate = plannedEndDate;
    this.analysis = analysis != null ? analysis : [];
    this.displayIndex = displayIndex;

    makeObservable(this, {
      id: observable,
      name: observable,
      distribution: observable,
      plannedCost: observable,
      plannedDuration: observable,
      plannedStartDate: observable,
      plannedEndDate: observable,
      // actualProgress: observable, //
      // plannedProgress: observable, //
    })
  }

  applyDistribution(distributionCode) {
    this.distribution = { code: distributionCode };
    ApplyDistributionToTask(this, distributionCode, (progressPlan) => {
      this.plannedProgress = progressPlan.map((entry) =>
        PlannedProgressEntry.fromJS(this.project, entry)
      );
      this.plannedProgress.forEach((element) => {
        element.task = this;
      });
    });
  }


  setTextStyle(depth) {
    return {
      fontSize: 11 + depth,
      format: "italic",
    };
  }

  buildReportRows(depth, specifiedPeriod) {
    let reportRow = {};
    let facSum = 0;
    const zeroPercent = decimalAsPercentFormat(0);
    const zeroDollars = 0.00;
    const zero = 0;
    reportRow.id = this.id;
    reportRow.directChildren = [];
    reportRow.displayName = " ".repeat(depth * 3) + this.code + " " + this.name;
    reportRow.progress = zeroPercent;
    reportRow.budgetSpent = zeroPercent;
    reportRow.plannedProgress = zeroPercent;
    reportRow.bac = zeroDollars;
    reportRow.actualCost = zeroDollars;
    reportRow.actualCostPeriod = zeroDollars;
    reportRow.cpiCumulativeIcon = "";
    reportRow.cpiCumulative = zero.toFixed(2);
    reportRow.spiCumulativeIcon = "";
    reportRow.spiCumulative = zero.toFixed(2);
    // The "facColumn" needs to be checked, not sure about it
    reportRow.fac = zeroDollars;
    reportRow.vac = zeroDollars;
    reportRow.costStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
    reportRow.schedStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
    reportRow.comment = "";
    reportRow.sortIndex = this.displayIndex;
    reportRow.styles = {}

    const analysis = this.analysis.find(e => e.period * 1 === specifiedPeriod * 1)

    if (analysis != null) {
      let result =
        analysis.actualProgress *
        analysis.budgetSpent *
        analysis.plannedProgress;
      let plannedProgressCol = analysis.plannedProgress;
      let bacCol = analysis.budgetAtCompletion;
      let progressCol = analysis.actualProgress;
      let earnedValueCol = analysis.earnedValue; // analysis.earnedValue = ("{0}*{1}", worksheet.Cells[row, this.progressColumn].Address, worksheet.Cells[row, this.bacColumn].Address)
      let actualCostCol = analysis.actualCost;
      let budgetSpentCol = analysis.budgetSpent; // analysis.budgetSpent = ("IFERROR({0}/{1},0)", worksheet.Cells[row, this.].Address, worksheet.Cells[row, this.bacColumn].Address)
      let actualCostPeriodCol = analysis.actualCostPeriod;
      let earnedValuePeriodCol = analysis.earnedValuePeriod;
      let cpiCumuCol = analysis.costPerformanceIndex; // (analysis.costPerformanceIndex).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),0)", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)

      // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
      // however as it is not being used in our current version, I have be commented it out
      //let plannedValueCol = plannedProgressCol * bacCol; // "{0}*{1}", worksheet.Cells[row, this.plannedProgressColumn].Address, worksheet.Cells[row, this.bacColumn].Address
      //let costVarianceCol = analysis.costVariance; // analysis.costVariance = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)
      //let earnedScheduleCol = analysis.earnedSchedule;
      //let scheduleVarianceCol = analysis.scheduleVarianceCost; // analysis.scheduleVarianceCost = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address)

      let cpiCumuColNeg = "";
      let spiCostCumuColNeg = "";

      // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
      // however as it is not being used in our current version, I have be commented it out
      //let spiTimeCumuColNeg = "";

      if (result !== 0 && !isNaN(result)) {
        // "IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address
        let calcCpi = 0;
        if (actualCostCol - actualCostPeriodCol > 0) {
          calcCpi =
            cpiCumuCol -
            (earnedValueCol - earnedValuePeriodCol) /
            (actualCostCol - actualCostPeriodCol);
        }

        let calcSpiCost =
          analysis.schedulePerformanceIndexCostPeriod -
          analysis.schedulePerformanceIndexCost; // "ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

        // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
        // however as it is not being used in our current version, I have be commented it out
        //let calcSpiTime = analysis.schedulePerformanceIndexTimePeriod - analysis.schedulePerformanceIndexTime; // "(ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

        if (calcCpi > 0) {
          cpiCumuColNeg = "\u25B2";
        } // "IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>0,\"\u25B2\",IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>=-0.0001,\"\u25AC\",\"\u25BC\"))", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address)
        else if (calcCpi >= -0.0001) {
          cpiCumuColNeg = "\u25AC";
        } else {
          cpiCumuColNeg = "\u25BC";
        }

        if (calcSpiCost > 0) {
          spiCostCumuColNeg = "\u25B2";
        } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
        else if (calcSpiCost >= -0.0001) {
          spiCostCumuColNeg = "\u25AC";
        } else {
          spiCostCumuColNeg = "\u25BC";
        }

        // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
        // however as it is not being used in our current version, I have be commented it out
        /*if (calcSpiTime > 0) {
          spiTimeCumuColNeg = "\u25B2";
        } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
        else if (calcSpiTime >= -0.0001) {
          spiTimeCumuColNeg = "\u25AC";
        } else {
          spiTimeCumuColNeg = "\u25BC";
        }*/


        // The "spiCostCol" and "spiTimeCol" needs to be checked, not sure about them
        let spiCol = analysis.schedulePerformanceIndexTime; // (analysis.schedulePerformanceIndexCost).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),ROUND(1+{2},2))", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address, worksheet.Cells[row, this.progressColumn].Address)

        // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
        // however as it is not being used in our current version, I have be commented it out
        //let spiTimeCol = analysis.schedulePerformanceIndexTime;
        let vacCol = analysis.varianceAtCompletionMean;
        let costStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
        let schedStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
        let commentCol = analysis.note;

        reportRow.progress = decimalAsPercentFormat(progressCol);
        reportRow.budgetSpent = decimalAsPercentFormat(budgetSpentCol);
        reportRow.plannedProgress = decimalAsPercentFormat(plannedProgressCol);
        
        reportRow.actualCost = (actualCostCol);
        reportRow.actualCostPeriod = (actualCostPeriodCol);
        reportRow.cpiCumulativeIcon = cpiCumuColNeg;
        reportRow.cpiCumulative =
          cpiCumuCol != null ? cpiCumuCol.toFixed(2) : cpiCumuCol;
        reportRow.spiCumulativeIcon = spiCostCumuColNeg;
        reportRow.spiCumulative =
          spiCol != null ? spiCol.toFixed(2) : spiCol;
        // The "facColumn" needs to be checked, not sure about it
        
        reportRow.vac = (vacCol);
        reportRow.costStatusIndicator = costStatusCol;
        reportRow.schedStatusIndicator = schedStatusCol;
        reportRow.comment = commentCol;

        facSum = analysis.forecastAtCompletionMean;

      }
      reportRow.bac = (bacCol);
      reportRow.fac = (analysis.budgetedRateForecast);

      let styleTasks = {};
      styleTasks.displayName = " " + this.code + " " + this.name + " ";
      styleTasks.fontSize = 11;
      styleTasks.fontStyle = depth < 2 ? "bold" : "italic";
      styleTasks.treeDepth = depth;
      styleTasks.childCount = 0;
      styleTasks.sortIndex = this.displayIndex;
      reportRow.styles = styleTasks;

    }

    return [reportRow];
  }

  countChildren() {
    return 0;
  }

  toJS() {
    return {
      id: this.id,
      name: this.name,
      distribution: this.distribution,
      plannedCost: this.plannedCost,
      plannedDuration: this.plannedDuration,
      plannedStartDate: this.plannedStartDate,
      plannedEndDate: this.plannedEndDate,
      analysis: this.analysis.map((entry) => entry.toJS()),
      displayIndex: this.displayIndex,
      code: this.code,
    };
  }

  static fromJS(store, object) {
    const task = new TaskAnalysis();
    if (object != null) {
      task.name = object.name;
      task.id = object.id;
      task.plannedCost = object.plannedCost;
      task.plannedDuration = object.plannedDuration;
      task.plannedStartDate = object.plannedStartDate;
      task.plannedEndDate = object.plannedEndDate;
      task.project = store;
      task.analysis = object.analysis.map((entry) => Analysis.fromJS(entry));
      task.displayIndex = object.displayIndex;
      task.code = object.code;
    }
    return task;
  }

  /*static fromJS_To2DArray(store, object, evaData, specifiedPeriod) {
    let tasksAndPackages = {};
    let analysisList = [];
    let facSum = 0;
    const zeroPercent = percentFormat(0);
    const zeroDollars = currencyFormat(0);
    if (object != null) {
      tasksAndPackages.displayName = " " + object.code + " " + object.name + " ";
      tasksAndPackages.progress = zeroPercent;
      tasksAndPackages.budgetSpent = zeroPercent;
      tasksAndPackages.plannedProgress = zeroPercent;
      tasksAndPackages.bac = zeroDollars;
      tasksAndPackages.actualCost = zeroDollars;
      tasksAndPackages.actualCostPeriod = zeroDollars;
      tasksAndPackages.cpiCumulativeIcon = "";
      tasksAndPackages.cpiCumulative = 0.0;
      tasksAndPackages.spiCumulativeIcon = "";
      tasksAndPackages.spiCumulative = 0.0;
      // The "facColumn" needs to be checked, not sure about it
      tasksAndPackages.fac = zeroDollars;
      tasksAndPackages.vac = zeroDollars;
      tasksAndPackages.costStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
      tasksAndPackages.schedStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
      tasksAndPackages.comment = "";
      tasksAndPackages.sortIndex = object.displayIndex;

      analysisList = object.analysis.map((entry) => Analysis.fromJS(entry));

      for (const analysis of analysisList) {
        if (analysis.period === specifiedPeriod) {
          let result =
            analysis.actualProgress *
            analysis.budgetSpent *
            analysis.plannedProgress;
          let plannedProgressCol = analysis.plannedProgress;
          let bacCol = analysis.budgetAtCompletion;
          let progressCol = analysis.actualProgress;
          let earnedValueCol = analysis.earnedValue; // analysis.earnedValue = ("{0}*{1}", worksheet.Cells[row, this.progressColumn].Address, worksheet.Cells[row, this.bacColumn].Address)
          let actualCostCol = analysis.actualCost;
          let budgetSpentCol = analysis.budgetSpent; // analysis.budgetSpent = ("IFERROR({0}/{1},0)", worksheet.Cells[row, this.].Address, worksheet.Cells[row, this.bacColumn].Address)
          let actualCostPeriodCol = analysis.actualCostPeriod;
          let earnedValuePeriodCol = analysis.earnedValuePeriod;
          let cpiCumuCol = analysis.costPerformanceIndex; // (analysis.costPerformanceIndex).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),0)", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)

          // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
          // however as it is not being used in our current version, I have be commented it out
          //let plannedValueCol = plannedProgressCol * bacCol; // "{0}*{1}", worksheet.Cells[row, this.plannedProgressColumn].Address, worksheet.Cells[row, this.bacColumn].Address
          //let costVarianceCol = analysis.costVariance; // analysis.costVariance = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)
          //let earnedScheduleCol = analysis.earnedSchedule;
          //let scheduleVarianceCol = analysis.scheduleVarianceCost; // analysis.scheduleVarianceCost = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address)

          let cpiCumuColNeg = "";
          let spiCostCumuColNeg = "";

          // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
          // however as it is not being used in our current version, I have be commented it out
          //let spiTimeCumuColNeg = "";

          if (result !== 0 && !isNaN(result)) {
            // "IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address
            let calcCpi = 0;
            if (actualCostCol - actualCostPeriodCol > 0) {
              calcCpi =
                cpiCumuCol -
                (earnedValueCol - earnedValuePeriodCol) /
                (actualCostCol - actualCostPeriodCol);
            }

            let calcSpiCost =
              analysis.schedulePerformanceIndexCostPeriod -
              analysis.schedulePerformanceIndexCost; // "ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

            // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
            // however as it is not being used in our current version, I have be commented it out
            //let calcSpiTime = analysis.schedulePerformanceIndexTimePeriod - analysis.schedulePerformanceIndexTime; // "(ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

            if (calcCpi > 0) {
              cpiCumuColNeg = "\u25B2";
            } // "IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>0,\"\u25B2\",IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>=-0.0001,\"\u25AC\",\"\u25BC\"))", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address)
            else if (calcCpi >= -0.0001) {
              cpiCumuColNeg = "\u25AC";
            } else {
              cpiCumuColNeg = "\u25BC";
            }

            if (calcSpiCost > 0) {
              spiCostCumuColNeg = "\u25B2";
            } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
            else if (calcSpiCost >= -0.0001) {
              spiCostCumuColNeg = "\u25AC";
            } else {
              spiCostCumuColNeg = "\u25BC";
            }

            // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
            // however as it is not being used in our current version, I have be commented it out
            if (calcSpiTime > 0) {
              spiTimeCumuColNeg = "\u25B2";
            } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
            else if (calcSpiTime >= -0.0001) {
              spiTimeCumuColNeg = "\u25AC";
            } else {
              spiTimeCumuColNeg = "\u25BC";
            }
          }

          // The "spiCostCol" and "spiTimeCol" needs to be checked, not sure about them
          let spiCostCol = analysis.schedulePerformanceIndexCost; // (analysis.schedulePerformanceIndexCost).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),ROUND(1+{2},2))", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address, worksheet.Cells[row, this.progressColumn].Address)

          // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
          // however as it is not being used in our current version, I have be commented it out
          //let spiTimeCol = analysis.schedulePerformanceIndexTime;
          let vacCol = analysis.varianceAtCompletionMean;
          let costStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
          let schedStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
          let commentCol = analysis.note;

          tasksAndPackages.progress = percentFormat(progressCol);
          tasksAndPackages.budgetSpent = percentFormat(budgetSpentCol);
          tasksAndPackages.plannedProgress = percentFormat(plannedProgressCol);
          tasksAndPackages.bac = currencyFormat(bacCol);
          tasksAndPackages.actualCost = currencyFormat(actualCostCol);
          tasksAndPackages.actualCostPeriod = currencyFormat(actualCostPeriodCol);
          tasksAndPackages.cpiCumulativeIcon = cpiCumuColNeg;
          tasksAndPackages.cpiCumulative =
            cpiCumuCol != null ? cpiCumuCol.toFixed(2) : cpiCumuCol;
          tasksAndPackages.spiCumulativeIcon = spiCostCumuColNeg;
          tasksAndPackages.spiCumulative =
            spiCostCol != null ? spiCostCol.toFixed(2) : spiCostCol;
          // The "facColumn" needs to be checked, not sure about it
          tasksAndPackages.fac = currencyFormat(analysis.forecastAtCompletionMean);
          tasksAndPackages.vac = currencyFormat(vacCol);
          tasksAndPackages.costStatusIndicator = costStatusCol;
          tasksAndPackages.schedStatusIndicator = schedStatusCol;
          tasksAndPackages.comment = commentCol;

          facSum = analysis.forecastAtCompletionMean;
        }
      }
    }
    evaData.push(tasksAndPackages); //ASK MATT: In which situation an object can be null? How do I handle it, in these scenario
    return { evaData: evaData, fac: facSum };
  }

  static fromJS_DetermineStyle(store, object, evaStyle, depth) {

    evaStyle.push(styleTasks);
    return { evaStyle: evaStyle, depth: depth };
  }*/
}


